import type { StorageMountCatalog } from './fileBrowserApi';
import { parseVirtualPath, toVirtualPath, type MountRoute } from './createFileBrowserProvider';

export function fileBrowserLastLocationKey(catalog: StorageMountCatalog = 'all'): string {
  return `jarvis.fileBrowser.lastPath.${catalog}`;
}

/** File Manager may report `\` separators; we always store `/`. */
export function normalizeFileBrowserPath(path: string | null | undefined): string {
  return (path ?? '').replace(/\\/g, '/').trim().replace(/^\/+|\/+$/g, '');
}

/** DevExtreme FileManager walks `currentPath` with backslashes. */
export function toFileManagerCurrentPath(path: string): string {
  return normalizeFileBrowserPath(path).replace(/\//g, '\\');
}

/** Keys match `toFileManagerItem` (`Mount/folder/child`), not just the last name. */
export function toFileManagerCurrentPathKeys(path: string): string[] {
  const normalized = normalizeFileBrowserPath(path);
  if (!normalized) return [];
  const parts = normalized.split('/').filter(Boolean);
  return parts.map((_, index) => parts.slice(0, index + 1).join('/'));
}

export function fileManagerDirectoryPath(directory: {
  path?: string;
  key?: string;
  pathKeys?: string[];
  dataItem?: { __path?: string; path?: string; key?: string };
} | null | undefined): string {
  const data = directory?.dataItem;
  const fromKeys = directory?.pathKeys?.length
    ? directory.pathKeys[directory.pathKeys.length - 1]
    : '';
  return normalizeFileBrowserPath(
    data?.__path ?? data?.path ?? directory?.path ?? data?.key ?? directory?.key ?? fromKeys
  );
}

type StoredLastLocation = {
  path: string;
  mountId?: string;
};

function readStoredLastLocation(catalog: StorageMountCatalog): StoredLastLocation {
  try {
    const raw = localStorage.getItem(fileBrowserLastLocationKey(catalog));
    if (!raw) return { path: '' };
    const trimmed = raw.trim();
    if (trimmed.startsWith('{')) {
      const parsed = JSON.parse(trimmed) as { path?: unknown; mountId?: unknown };
      return {
        path: normalizeFileBrowserPath(typeof parsed.path === 'string' ? parsed.path : ''),
        mountId: typeof parsed.mountId === 'string' ? parsed.mountId : undefined
      };
    }
    return { path: normalizeFileBrowserPath(trimmed) };
  } catch {
    return { path: '' };
  }
}

export function readLastLocation(catalog: StorageMountCatalog = 'all'): string {
  return readStoredLastLocation(catalog).path;
}

export function writeLastLocation(catalog: StorageMountCatalog, path: string, mountId?: string): void {
  try {
    const normalized = normalizeFileBrowserPath(path);
    const value =
      mountId && normalized
        ? JSON.stringify({ path: normalized, mountId })
        : normalized;
    localStorage.setItem(fileBrowserLastLocationKey(catalog), value);
  } catch {
    // ignore quota / private mode errors
  }
}

/**
 * Persist the folder the user is in. The File Manager fires an empty-path
 * "root" event on startup; ignore that so it cannot wipe the saved location.
 */
export function persistNavigatedLocation(
  catalog: StorageMountCatalog,
  rawPath: string | null | undefined,
  hasEnteredFolder: boolean,
  routes: readonly MountRoute[] = []
): { path: string; hasEnteredFolder: boolean } {
  const path = normalizeFileBrowserPath(rawPath);
  if (path) {
    writeLastLocation(catalog, path, parseVirtualPath(path, routes)?.mount.id);
    return { path, hasEnteredFolder: true };
  }
  if (hasEnteredFolder) {
    writeLastLocation(catalog, '');
  }
  return { path: '', hasEnteredFolder };
}

/** Restore a saved folder only when its mount still exists in this catalog. */
export function resolveLastLocation(
  catalog: StorageMountCatalog,
  routes: readonly MountRoute[]
): string {
  const stored = readStoredLastLocation(catalog);
  if (!stored.path && !stored.mountId) return '';

  const byPath = parseVirtualPath(stored.path, routes);
  if (byPath) return toVirtualPath(byPath.mount.key, byPath.relative);

  const mount = stored.mountId ? routes.find((route) => route.id === stored.mountId) : undefined;
  if (!mount) return '';

  const parts = stored.path.split('/').filter(Boolean);
  if (parts.length <= 1) return mount.key;
  return toVirtualPath(mount.key, parts.slice(1).join('/'));
}
